// src/pages/OnboardingDepositPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
// §6 — Dépôt des données. Première action du collectionneur : récupérer l'existant.
// L'utilisateur dépose PDF / photos / Excel ; on stocke dans le bucket privé
// `onboarding_deposits` (chemin <uid>/<deposit_id>/<fichier>), on enregistre un
// batch + ses items, puis on déclenche l'analyse IA (edge function analyze-deposit)
// et on redirige vers l'écran de recommandation (§7-8).
//
// Aucune écriture sensible : RLS owner-only sur onboarding_deposits(_items).
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { resolveProfileType, metierHomeRoute } from "../utils/onboardingProfile";

const ACCEPT = ".pdf,image/*,.csv,.xls,.xlsx";
const MAX_FILES = 300;

// Sources de dépôt (§6). Seul « upload » est actif ; les connecteurs viendront
// se brancher ici en réutilisant le même pipeline (bucket → items → analyze).
const SOURCES = [
  { key: "upload", icon: "📎" },
  { key: "dropbox", icon: "🗂️", soon: true },
  { key: "drive", icon: "📁", soon: true },
  { key: "email", icon: "✉️", soon: true },
];

export default function OnboardingDepositPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState("");
  const [source, setSource] = useState("upload");

  const profileType = useMemo(() => resolveProfileType(profile), [profile]);

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []).slice(0, MAX_FILES);
    setFiles(picked);
    setError("");
  };

  const totalMB = useMemo(
    () => (files.reduce((s, f) => s + f.size, 0) / (1024 * 1024)).toFixed(1),
    [files]
  );

  const handleUpload = async () => {
    if (!user?.id || files.length === 0) return;
    setBusy(true);
    setError("");
    setProgress({ done: 0, total: files.length });

    try {
      // 1) Créer le batch
      const { data: dep, error: depErr } = await supabase
        .from("onboarding_deposits")
        .insert({ user_id: user.id, profile_type: profileType, source, status: "uploading" })
        .select("id")
        .single();
      if (depErr) throw depErr;
      const depositId = dep.id;

      // 2) Upload + item par fichier
      let done = 0;
      for (const file of files) {
        const safeName = file.name.replace(/[^\w.-]+/g, "_");
        const path = `${user.id}/${depositId}/${crypto.randomUUID()}_${safeName}`;
        const { error: upErr } = await supabase.storage
          .from("onboarding_deposits")
          .upload(path, file, { upsert: false });
        if (upErr) throw upErr;

        const { error: itErr } = await supabase.from("onboarding_deposit_items").insert({
          deposit_id: depositId,
          user_id: user.id,
          file_path: path,
          file_name: file.name,
          mime: file.type || null,
          size_bytes: file.size,
        });
        if (itErr) throw itErr;

        done += 1;
        setProgress({ done, total: files.length });
      }

      // 3) Vers la recommandation. C'est CETTE page qui pilote l'analyse par
      //    lots (chunks) jusqu'à complétion — auto-réparant et scalable.
      navigate(`/onboarding/recommendation?d=${depositId}`, { replace: true });
    } catch (e) {
      console.error("[deposit] upload error:", e);
      setError(e.message || t("onboarding.deposit.error"));
      setBusy(false);
    }
  };

  const skip = () => {
    // On ne force pas : retour à l'espace du métier (pas seulement /my-collection).
    window.location.assign(metierHomeRoute(profileType));
  };

  return (
    <Shell>
      <p style={eyebrow}>{t("onboarding.deposit.eyebrow")}</p>
      <h1 style={h1}>{t(`onboarding.deposit.byMetier.${profileType}.title`, { defaultValue: t("onboarding.deposit.title") })}</h1>
      <p style={p}>{t(`onboarding.deposit.byMetier.${profileType}.intro`, { defaultValue: t("onboarding.deposit.intro") })}</p>

      {/* Sélecteur de source (§6). Upload actif ; connecteurs à venir. */}
      <div style={tabBar}>
        {SOURCES.map((s) => (
          <button
            key={s.key}
            onClick={() => { if (!busy) { setSource(s.key); setError(""); } }}
            style={source === s.key ? tabActive : tab}
            disabled={busy}
          >
            <span aria-hidden style={{ marginRight: 6 }}>{s.icon}</span>
            {t(`onboarding.deposit.sources.${s.key}`)}
          </button>
        ))}
      </div>

      {source === "upload" ? (
        <>
          <label style={dropzone}>
            <input type="file" multiple accept={ACCEPT} onChange={onPick} style={{ display: "none" }} disabled={busy} />
            <span style={{ fontSize: "2rem" }}>📎</span>
            <span style={{ fontWeight: 700, marginTop: "0.5rem" }}>
              {files.length ? t("onboarding.deposit.filesPicked", { count: files.length, size: totalMB }) : t("onboarding.deposit.choose")}
            </span>
            <span style={{ fontSize: "0.8rem", color: "#8a8378", marginTop: "0.35rem" }}>
              {t("onboarding.deposit.hint", { max: MAX_FILES })}
            </span>
          </label>

          {files.length > 0 && (
            <ul style={fileList}>
              {files.slice(0, 8).map((f, i) => (
                <li key={i} style={fileRow}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                  <span style={{ color: "#8a8378", flexShrink: 0 }}>{(f.size / 1024).toFixed(0)} Ko</span>
                </li>
              ))}
              {files.length > 8 && <li style={{ ...fileRow, color: "#8a8378" }}>{t("onboarding.deposit.more", { count: files.length - 8 })}</li>}
            </ul>
          )}

          {busy && (
            <div style={{ margin: "1rem 0" }}>
              <div style={progressTrack}>
                <div style={{ ...progressFill, width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
              </div>
              <p style={{ fontSize: "0.82rem", color: "#6b6459", marginTop: "0.4rem" }}>
                {t("onboarding.deposit.uploading", { done: progress.done, total: progress.total })}
              </p>
            </div>
          )}

          {error && <p style={errorStyle}>{error}</p>}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "1.5rem", gap: "0.6rem" }}>
            <button style={ghost} onClick={skip} disabled={busy}>{t("onboarding.deposit.later")}</button>
            <button style={primary} onClick={handleUpload} disabled={busy || files.length === 0}>
              {busy ? t("onboarding.deposit.submitting") : t("onboarding.deposit.submit")}
            </button>
          </div>
        </>
      ) : (
        <div style={soonPanel}>
          <span style={{ fontSize: "2rem" }}>🚧</span>
          <p style={{ fontSize: "0.95rem", color: "#4a453d", margin: "0.75rem 0 0", lineHeight: 1.6 }}>
            {t("onboarding.deposit.comingSoon", { source: t(`onboarding.deposit.sources.${source}`) })}
          </p>
          <div style={{ display: "flex", gap: "0.6rem", marginTop: "1.25rem" }}>
            <button style={ghost} onClick={skip}>{t("onboarding.deposit.later")}</button>
            <button style={primary} onClick={() => setSource("upload")}>{t("onboarding.deposit.chooseUpload")}</button>
          </div>
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(160deg,#f9f7f4,#f1ede6)", display: "flex", alignItems: "center", justifyContent: "center", padding: "2.5rem 1.25rem", fontFamily: "Georgia, serif" }}>
      <div style={{ width: "100%", maxWidth: 560, background: "#fff", border: "1px solid #e5e0d8", borderRadius: 18, padding: "2.5rem" }}>
        {children}
      </div>
    </div>
  );
}

const eyebrow = { fontSize: "0.72rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "#c9a961", margin: 0 };
const h1 = { fontSize: "1.7rem", fontWeight: 700, lineHeight: 1.2, color: "#1a1a1a", margin: "0.6rem 0 0.75rem" };
const p = { fontSize: "0.95rem", lineHeight: 1.6, color: "#4a453d", margin: "0 0 1.5rem" };
const tabBar = { display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "1.1rem" };
const tab = { background: "#faf8f5", color: "#6b6459", border: "1px solid #e5e0d8", cursor: "pointer", padding: "0.5rem 0.85rem", borderRadius: 999, fontSize: "0.82rem", fontFamily: "inherit" };
const tabActive = { ...tab, background: "#1a1a1a", color: "#f9f7f4", borderColor: "#1a1a1a" };
const soonPanel = { textAlign: "center", background: "#faf8f5", border: "2px dashed #d8d2c8", borderRadius: 14, padding: "2rem 1.25rem" };
const dropzone = { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", background: "#faf8f5", border: "2px dashed #d8d2c8", borderRadius: 14, padding: "2rem 1rem", cursor: "pointer" };
const fileList = { listStyle: "none", padding: 0, margin: "1rem 0 0", display: "flex", flexDirection: "column", gap: "0.35rem" };
const fileRow = { display: "flex", justifyContent: "space-between", gap: "0.75rem", fontSize: "0.85rem", color: "#4a453d", borderBottom: "1px solid #f0ece5", paddingBottom: "0.3rem" };
const progressTrack = { width: "100%", height: 6, background: "#eee8df", borderRadius: 999, overflow: "hidden" };
const progressFill = { height: "100%", background: "#c9a961", borderRadius: 999, transition: "width 0.2s ease" };
const primary = { background: "#1a1a1a", color: "#f9f7f4", border: "none", cursor: "pointer", padding: "0.85rem 1.75rem", borderRadius: 10, fontSize: "0.95rem", fontFamily: "inherit" };
const ghost = { background: "transparent", color: "#6b6459", border: "1px solid #d8d2c8", cursor: "pointer", padding: "0.85rem 1.4rem", borderRadius: 10, fontSize: "0.9rem", fontFamily: "inherit" };
const errorStyle = { color: "#b42318", fontSize: "0.85rem", margin: "0.75rem 0 0" };
